"use client";
import Link from "next/link";
import { toast } from "sonner";
import { useState } from "react";
import MessageSquare from "lucide-react/dist/esm/icons/message-square";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { ListSkeleton } from "@/components/asset-detail/ListSkeleton";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Separator } from "@/components/ui/separator";
import { UserBadges } from "@/components/ui/user-badges";
import { useSession } from "@/contexts/SessionContext";
import dynamic from "next/dynamic";
import { useProfile } from "@/contexts/ProfileContext";

// Loaded only behind the admin gate below — the moderation mutation and its
// confirm dialog stay out of the public asset-page bundle.
const QuestionAdminActionsInner = dynamic(
  () =>
    import("@/components/context-actions/QuestionAdminActionsInner").then(
      (m) => m.QuestionAdminActionsInner,
    ),
  { ssr: false },
);

/** Admin-only inline moderation for a single question. */
function QuestionAdminActions({ questionId }: { questionId: string }) {
  const { isAdmin } = useProfile();
  if (!isAdmin) return null;
  return <QuestionAdminActionsInner questionId={questionId} />;
}
import { useAuthModal } from "@/contexts/AuthModalContext";
import { logEvent } from "@/lib/events";
import {
  useAssetQuestions,
  useCreateQuestion,
  useAnswerQuestion,
  useCreateQuestionReply,
  type Question,
  type QuestionParticipant,
} from "@/hooks/useAssetQuestions";
import { useUserBadgesBatch } from "@/hooks/useUserBadges";
import { humanizeError } from "@/lib/error-humanizer";
import { format } from "date-fns";

interface QuestionsSectionProps {
  assetId: string;
  sellerId: string;
}

function initialsOf(name: string): string {
  return name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
}

/**
 * One message inside a thread — the seller's answer or any later reply.
 * Indented under the question so a long back-and-forth still reads as one
 * conversation rather than a flat list.
 */
function ThreadMessage({
  author,
  body,
  at,
  roleLabel,
  roles,
}: {
  author: QuestionParticipant | null;
  body: string;
  at: string | null;
  /** "Seller" / "Admin" — omitted for the buyer's own follow-ups. */
  roleLabel: string | null;
  roles?: string[];
}) {
  const displayName = author?.display_name || author?.username || "Anonymous";
  const userLink = author?.username ? `/store/${author.username}` : null;

  return (
    <div
      className={`ml-11 border-l-2 pl-4 ${roleLabel ? "border-primary/20" : "border-border"}`}
    >
      <div className="flex flex-wrap items-center gap-2">
        <Avatar className="h-6 w-6">
          <AvatarImage src={author?.avatar_url || undefined} alt={displayName} />
          <AvatarFallback className="text-2xs">{initialsOf(displayName)}</AvatarFallback>
        </Avatar>
        {userLink ? (
          <Link
            href={userLink}
            className="text-sm font-medium transition-colors hover:text-primary"
          >
            {displayName}
          </Link>
        ) : (
          <span className="text-sm font-medium">{displayName}</span>
        )}
        {roleLabel && (
          <span className="text-xs font-medium text-primary">{roleLabel}</span>
        )}
        <UserBadges
          user={{
            is_seller: author?.is_seller ?? null,
            seller_verified: author?.seller_verified ?? null,
            is_early_founder: author?.is_early_founder ?? null,
            roles,
          }}
          size="sm"
        />
        {at && (
          <span className="text-xs text-muted-foreground">
            {format(new Date(at), "MMM d, yyyy")}
          </span>
        )}
      </div>
      <p className="mt-1 whitespace-pre-wrap text-muted-foreground">{body}</p>
    </div>
  );
}

function QuestionItem({
  question,
  isOwner,
  isAdmin,
  viewerProfileId,
  sellerId,
  roles,
  rolesMap,
}: {
  question: Question;
  isOwner: boolean;
  isAdmin: boolean;
  viewerProfileId: string | null;
  sellerId: string;
  roles?: string[];
  rolesMap?: Record<string, string[]>;
}) {
  const answerQuestion = useAnswerQuestion();
  const createReply = useCreateQuestionReply();
  const [showAnswerForm, setShowAnswerForm] = useState(false);
  const [answer, setAnswer] = useState("");

  // Author profile can be null (deleted/anonymized account) — same
  // normalization as ReviewItem so the question still renders as "Anonymous".
  const author = question.user && !Array.isArray(question.user) ? question.user : null;
  const displayName = author?.display_name || author?.username || "Anonymous";
  const initials = initialsOf(displayName);

  const isAsker = !!viewerProfileId && author?.id === viewerProfileId;
  // A public listing anyone can ask on, but a thread only its participants can
  // post into — see the "Thread participants can reply" RLS policy.
  const canRespond = isOwner || isAdmin || isAsker;
  // The seller's FIRST response still fills `questions.answer`: the unanswered
  // badge, the seller inbox and the public REST endpoint all key off that
  // column. Everything after it is a reply row.
  const isFirstSellerAnswer = !question.answer && (isOwner || isAdmin);
  const isPending = answerQuestion.isPending || createReply.isPending;

  const handleSubmitAnswer = async (e: React.FormEvent) => {
    e.preventDefault();
    const body = answer.trim();
    if (!body) return;

    try {
      if (isFirstSellerAnswer) {
        await answerQuestion.mutateAsync({ questionId: question.id, answer: body });
      } else {
        await createReply.mutateAsync({ questionId: question.id, body });
      }
      setShowAnswerForm(false);
      setAnswer("");
      toast.success(isFirstSellerAnswer ? "Answer submitted successfully!" : "Reply sent");
    } catch (error) {
      console.error("Error submitting message:", error);
      toast.error(humanizeError(error, isFirstSellerAnswer ? "submitting your answer" : "sending your reply"));
    }
  };

  const userLink = author?.username ? `/store/${author.username}` : null;

  return (
    <div className="space-y-4">
      {/* Question */}
      <div className="flex items-start gap-3">
        {userLink ? (
          <Link href={userLink}>
            <Avatar className="h-8 w-8 hover:ring-2 hover:ring-primary transition-all">
              <AvatarImage src={author?.avatar_url || undefined} alt={displayName} />
              <AvatarFallback className="text-xs">{initials}</AvatarFallback>
            </Avatar>
          </Link>
        ) : (
          <Avatar className="h-8 w-8">
            <AvatarImage src={author?.avatar_url || undefined} alt={displayName} />
            <AvatarFallback className="text-xs">{initials}</AvatarFallback>
          </Avatar>
        )}
        <div className="flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            {userLink ? (
              <Link
                href={userLink}
                className="font-medium text-sm hover:text-primary transition-colors"
              >
                {displayName}
              </Link>
            ) : (
              <span className="font-medium text-sm">{displayName}</span>
            )}
            <UserBadges
              user={{
                is_seller: author?.is_seller ?? null,
                seller_verified: author?.seller_verified ?? null,
                is_early_founder: author?.is_early_founder ?? null,
                roles,
              }}
              size="sm"
            />
            <span className="text-xs text-muted-foreground">
              {format(new Date(question.created_at), "MMM d, yyyy")}
            </span>
          </div>
          <p className="mt-1">{question.question}</p>
        </div>
        {/* Renders nothing for non-admins. */}
        <QuestionAdminActions questionId={question.id} />
      </div>

      {/* Thread: the seller's first answer, then every later message. */}
      {question.answer && (
        <ThreadMessage
          author={question.answered_by_user && !Array.isArray(question.answered_by_user) ? question.answered_by_user : null}
          body={question.answer}
          at={question.answered_at}
          roleLabel={
            question.answered_by_user?.id && question.answered_by_user.id !== sellerId
              ? "Response"
              : "Seller"
          }
          roles={rolesMap?.[question.answered_by_user?.user_id || ""] || []}
        />
      )}

      {(question.replies ?? []).map((reply) => (
        <ThreadMessage
          key={reply.id}
          author={reply.author}
          body={reply.body}
          at={reply.created_at}
          roleLabel={reply.author?.id === sellerId ? "Seller" : null}
          roles={rolesMap?.[reply.author?.user_id || ""] || []}
        />
      ))}

      {/* Composer — participants only; everyone else just reads the thread. */}
      {canRespond ? (
        showAnswerForm ? (
          <form onSubmit={handleSubmitAnswer} className="ml-11 space-y-2">
            <Textarea
              value={answer}
              onChange={(e) => setAnswer(e.target.value)}
              placeholder={isFirstSellerAnswer ? "Write your answer…" : "Write a reply…"}
              rows={3}
              aria-label={isFirstSellerAnswer ? "Your answer" : "Your reply"}
            />
            <div className="flex gap-2">
              <Button type="submit" size="sm" disabled={isPending}>
                {isPending ? "Sending…" : isFirstSellerAnswer ? "Submit Answer" : "Send Reply"}
              </Button>
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => setShowAnswerForm(false)}
              >
                Cancel
              </Button>
            </div>
          </form>
        ) : (
          <Button
            variant="ghost"
            size="sm"
            className="ml-11"
            onClick={() => setShowAnswerForm(true)}
          >
            {isFirstSellerAnswer
              ? "Answer this question"
              : question.answer || (question.replies ?? []).length > 0
                ? "Reply to this thread"
                : "Add a follow-up"}
          </Button>
        )
      ) : !question.answer && (question.replies ?? []).length === 0 ? (
        <p className="ml-11 text-sm italic text-muted-foreground">Awaiting seller response</p>
      ) : null}
    </div>
  );
}

export function QuestionsSection({ assetId, sellerId }: QuestionsSectionProps) {
  const { user } = useSession();
  const { profile, isAdmin } = useProfile();
  const { openAuthModal } = useAuthModal();
  const { data: questions, isLoading } = useAssetQuestions(assetId);
  const createQuestion = useCreateQuestion();

  // Batch fetch roles for everyone who appears in a thread — asker, answerer
  // and every reply author — so a badge doesn't vanish halfway down a
  // conversation.
  const questionUserIds = Array.from(
    new Set(
      (questions || []).flatMap((q) => [
        q.user?.user_id,
        q.answered_by_user?.user_id,
        ...(q.replies ?? []).map((r) => r.author?.user_id),
      ]),
    ),
  ).filter(Boolean) as string[];
  const { data: rolesMap } = useUserBadgesBatch(questionUserIds);

  const [showForm, setShowForm] = useState(false);
  const [question, setQuestion] = useState("");

  const isOwner = profile?.id === sellerId;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!question.trim()) return;

    try {
      await createQuestion.mutateAsync({
        assetId,
        question,
      });
      setShowForm(false);
      setQuestion("");
      logEvent("question_submitted", { asset_id: assetId }, { severity: "medium" });
      toast.success("Question submitted successfully!");
    } catch (error) {
      console.error("Error submitting question:", error);
      toast.error(humanizeError(error, "submitting your question"));
    }
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <div className="flex items-center gap-2">
          <MessageSquare className="h-5 w-5" />
          <CardTitle>Questions & Answers</CardTitle>
        </div>
        {!showForm && (
          <Button onClick={() => {
            if (!user) {
              openAuthModal({
                message: "Ask a question",
                onSuccess: () => setShowForm(true),
              });
              return;
            }
            setShowForm(true);
          }}>Ask a Question</Button>
        )}
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Question Form */}
        {showForm && (
          <form onSubmit={handleSubmit} className="space-y-4 p-4 bg-muted rounded-lg">
            <div>
              <label htmlFor="asset-question-input" className="text-sm font-medium mb-2 block">Your Question</label>
              <Textarea
                id="asset-question-input"
                value={question}
                onChange={(e) => setQuestion(e.target.value)}
                placeholder="Ask the seller a question about this asset…"
                rows={3}
              />
            </div>
            <div className="flex gap-2">
              <Button type="submit" disabled={createQuestion.isPending}>
                {createQuestion.isPending ? "Submitting…" : "Submit Question"}
              </Button>
              <Button type="button" variant="outline" onClick={() => setShowForm(false)}>
                Cancel
              </Button>
            </div>
          </form>
        )}

        {/* Questions List */}
        {isLoading ? (
          <ListSkeleton rows={2} />
        ) : questions && questions.length > 0 ? (
          <div className="space-y-6">
            {questions.map((q, index) => (
              <div key={q.id}>
                <QuestionItem
                  question={q}
                  isOwner={isOwner}
                  isAdmin={isAdmin}
                  viewerProfileId={profile?.id ?? null}
                  sellerId={sellerId}
                  roles={rolesMap?.[q.user?.user_id || ""] || []}
                  rolesMap={rolesMap}
                />
                {index < questions.length - 1 && <Separator className="mt-6" />}
              </div>
            ))}
          </div>
        ) : (
          <p className="text-muted-foreground text-center py-8">
            No questions yet. Be the first to ask!
          </p>
        )}
      </CardContent>
    </Card>
  );
}
